"use client";

import { Download, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Dropzone } from "@/components/Dropzone";
import { FileList } from "@/components/FileList";
import { Button, Card, ErrorNote, Field, Progress, Select } from "@/components/ui";
import { baseName, canvasToBlob, download, formatBytes, loadImage } from "@/lib/utils";
import { GORSEL_KABUL } from "@/lib/dosyaTurleri";
import { getJSZip } from "@/lib/agir";

const TARGETS = [
  { type: "image/jpeg", ext: "jpg", label: "JPG — en uyumlu" },
  { type: "image/png", ext: "png", label: "PNG — kayıpsız, şeffaflık destekler" },
  { type: "image/webp", ext: "webp", label: "WebP — web için en küçük" },
  { type: "image/avif", ext: "avif", label: "AVIF — en yeni, en küçük" },
] as const;

const ACCEPT = GORSEL_KABUL;

/** Tarayıcının o formatta kodlama yapıp yapamadığını sınar. */
function canEncode(type: string) {
  const c = document.createElement("canvas");
  c.width = c.height = 1;
  return c.toDataURL(type).startsWith(`data:${type}`);
}

export function ConvertTool() {
  const [files, setFiles] = useState<File[]>([]);
  const [target, setTarget] = useState<string>("image/webp");
  const [quality, setQuality] = useState(0.85);
  const [supported, setSupported] = useState<string[]>([]);
  const [results, setResults] = useState<{ name: string; blob: Blob }[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState("");

  // canvas yalnızca tarayıcıda var; desteklenen biçimler ilk render'dan sonra belirlenir
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSupported(TARGETS.map((t) => t.type).filter(canEncode));
  }, []);

  const lossless = target === "image/png";

  async function run() {
    setProgress(0);
    setError("");
    setResults([]);
    const out: { name: string; blob: Blob }[] = [];
    try {
      const ext = TARGETS.find((t) => t.type === target)!.ext;
      for (const [i, file] of files.entries()) {
        const img = await loadImage(file);
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d")!;
        if (target === "image/jpeg") {
          ctx.fillStyle = "#ffffff"; // JPEG şeffaflık tutmaz
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0);
        out.push({
          name: `${baseName(file.name)}.${ext}`,
          blob: await canvasToBlob(canvas, target, lossless ? undefined : quality),
        });
        setProgress((i + 1) / files.length);
      }
      setResults(out);
    } catch {
      setError("Dönüştürme sırasında bir sorun oluştu. Dosyaları kontrol edin.");
    } finally {
      setProgress(null);
    }
  }

  async function downloadAll() {
    const JSZip = await getJSZip();
    if (results.length === 1) return download(results[0].blob, results[0].name);
    const zip = new JSZip();
    results.forEach((r) => zip.file(r.name, r.blob));
    download(await zip.generateAsync({ type: "blob" }), "donusturulmus-gorseller.zip");
  }

  if (!files.length) {
    return (
      <>
        <Dropzone
          accept={ACCEPT}
          multiple
          onFiles={setFiles}
          label="Görselleri buraya sürükleyin"
          hint="JPG, JFIF, PNG, WebP, AVIF, GIF ve BMP okunabilir. İnternetten indirdiğiniz açılmayan görseller de olur."
        />
        <div className="mt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      </>
    );
  }

  return (
    <div className="space-y-4">
      <FileList files={files} onChange={setFiles} reorderable={false} />

      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border py-3 text-sm text-muted transition hover:border-brand/50 hover:text-foreground">
        <Plus className="size-4" />
        Görsel ekle
        <input
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            setFiles([...files, ...Array.from(e.target.files ?? [])]);
            setResults([]);
            e.target.value = "";
          }}
        />
      </label>

      <Card className="grid gap-5 sm:grid-cols-2">
        <Field label="Hedef biçim" hint="Tarayıcınızın desteklemediği biçimler listelenmez.">
          <Select
            value={target}
            onChange={(e) => {
              setTarget(e.target.value);
              setResults([]);
            }}
          >
            {TARGETS.filter((t) => supported.includes(t.type)).map((t) => (
              <option key={t.type} value={t.type}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        {!lossless && (
          <Field label={`Kalite — %${Math.round(quality * 100)}`} hint="PNG'de kalite ayarı yoktur.">
            <input
              type="range"
              min={40}
              max={100}
              step={5}
              value={quality * 100}
              onChange={(e) => setQuality(Number(e.target.value) / 100)}
              className="w-full accent-[var(--brand)]"
            />
          </Field>
        )}
      </Card>

      {progress !== null && <Progress value={progress} label="Dönüştürülüyor…" />}
      <ErrorNote>{error}</ErrorNote>

      {results.length > 0 && (
        <ul className="space-y-2">
          {results.map((r) => (
            <li
              key={r.name}
              className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{r.name}</p>
                <p className="text-xs text-muted">{formatBytes(r.blob.size)}</p>
              </div>
              <Button variant="ghost" onClick={() => download(r.blob, r.name)}>
                <Download className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-3">
        <Button onClick={run} loading={progress !== null}>
          {files.length} görseli dönüştür
        </Button>
        {results.length > 0 && (
          <Button variant="outline" onClick={downloadAll}>
            <Download className="size-4" />
            {results.length > 1 ? "Hepsini indir (ZIP)" : "İndir"}
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={() => {
            setFiles([]);
            setResults([]);
          }}
        >
          Temizle
        </Button>
      </div>
    </div>
  );
}
